import { Link } from 'react-router-dom';
import { Loader2, FileDown } from 'lucide-react';
import { SURTITRE, pillSecondary } from '../../ui/pills';
import { Checkbox } from '../../ui/Field';
import { TuileChiffre } from '../../ui/TuileChiffre';
import { sortByCategoryOrder } from '../../../lib/categoryOrder';

export interface FormuleInfos {
    /** Formules du catalogue rattachées à la collecte. */
    menus: { id: string; name: string; structure: { category: string; dish_ids: string[] }[] }[];
    /** Plats du catalogue, pour retrouver les noms. */
    items: { id: string; name: string; category: string }[];
    guestChoiceMode: 'convive' | 'organisateur';
    fixedDishIds: Record<string, string>;
    /** Réponses verrouillées (validation faite). */
    validee: boolean;
    /** Nombre de plats réellement autorisés aux convives (quota renseigné ou « Illimité » coché). */
    platsAutorises: number;
}

const fmtDateHeure = (d?: string | null) => {
    if (!d) return '';
    try {
        const x = new Date(d);
        return `${x.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} à ${x.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } catch { return ''; }
};

/**
 * « La formule retenue » (maquette 18b) : la ou les formules rattachées, le cas du menu unique imposé
 * (« aucun choix individuel, la cuisine produit N fois le même service »), et les plats par catégorie.
 * Le prix par convive de la maquette n'existe pas sur une formule du catalogue : il n'est pas inventé.
 * « Changer de formule » mène à la page de configuration, où ce réglage vit déjà.
 */
export function FormuleRetenueCard({ eventId, infos, couverts }: { eventId: string; infos: FormuleInfos; couverts: number }) {
    const menus = infos.menus;
    const nomPlat = (id: string) => infos.items.find((i) => i.id === id)?.name || '';
    const impose = infos.guestChoiceMode === 'organisateur';
    // Plats affichés : ceux fixés par catégorie en menu imposé ; sinon les plats des formules, par catégorie.
    const lignes: [string, string][] = impose
        ? sortByCategoryOrder(Object.keys(infos.fixedDishIds)).map((cat) => [cat, nomPlat(infos.fixedDishIds[cat])] as [string, string]).filter(([, n]) => n)
        : (() => {
            const parCat: Record<string, string[]> = {};
            menus.forEach((m) => m.structure.forEach((s) => s.dish_ids.forEach((id) => {
                const it = infos.items.find((i) => i.id === id);
                if (!it) return;
                const cat = it.category || s.category || 'Autre';
                (parCat[cat] ||= []).push(it.name);
            })));
            return sortByCategoryOrder(Object.keys(parCat)).map((cat) => [cat, Array.from(new Set(parCat[cat])).join(' · ')] as [string, string]);
        })();

    return (
        <section className="p-4 rounded-xl bg-surface-soft border border-border-soft">
            <div className="flex items-baseline justify-between gap-2.5 flex-wrap mb-3">
                <span className={SURTITRE}>La formule retenue</span>
                <span className={`inline-flex items-center h-[22px] px-[9px] rounded-md border text-[10px] font-bold uppercase tracking-[.06em] whitespace-nowrap ${
                    infos.validee ? 'bg-state-ok-soft border-state-ok/30 text-state-ok' : menus.length ? 'bg-state-info-soft border-state-info/30 text-state-info' : 'bg-surface border-border text-content-muted'}`}>
                    {infos.validee ? 'Validée' : menus.length ? 'En collecte' : 'À configurer'}
                </span>
            </div>
            {menus.length === 0 ? (
                <p className="text-[12px] text-content-muted text-pretty">Aucune formule n'est encore rattachée à cette privatisation — ouvrez « Configurer le menu » pour en choisir une, ou pour en créer une nouvelle.</p>
            ) : (
                <div className="px-3.5 py-[13px] rounded-[10px] bg-surface border border-border">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <span className="text-[14px] font-bold tracking-[-0.01em] text-content">{menus.map((m) => m.name).join(' + ')}</span>
                        {couverts > 0 && <span className="font-mono text-[13px] tabular-nums text-content shrink-0">{couverts} {uniteCouverts(couverts)}</span>}
                    </div>
                    <p className="mt-1.5 text-[12px] leading-[1.5] text-content-muted text-pretty">
                        {impose
                            ? `Menu unique imposé par le client — aucun choix individuel. La cuisine produit ${couverts || 'N'} fois le même service.`
                            : 'Chaque convive choisit son plat lui-même depuis son lien ; le fichier du client ne sert qu\'à l\'inviter.'}
                    </p>
                    {lignes.length > 0 && (
                        <div className="flex flex-col mt-[11px] pt-[11px] border-t border-border-soft">
                            {lignes.map(([cat, plats]) => (
                                <div key={cat} className="flex items-baseline gap-2.5 py-[5px]">
                                    <span className="w-[58px] shrink-0 text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted">{cat}</span>
                                    <span className="text-[12.5px] text-content min-w-0">{plats}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {/* On ne « change » pas une formule qui n'existe pas : tant que rien n'est rattaché, le bouton
                nomme le geste à faire. Emeni, 18/09 — la configuration du menu doit se faire ici, dans la
                fiche de Menus & Convives, sans passer par Suivi privatisation. */}
            {/* RATTACHER LA FORMULE NE SUFFIT PAS : un plat dont le quota est laissé vide n'est pas proposé
                (règle voulue, « ceux que j'ai laissé vide sont ceux qui ne sont pas autorisés »). Rien ne le
                disait ici : la carte affichait les plats de la formule, l'état passait à « En collecte », et
                le convive recevait une page où il n'y avait rien à choisir. Vu en simulant une privatisation
                de bout en bout le 18/09. */}
            {menus.length > 0 && infos.guestChoiceMode === 'convive' && infos.platsAutorises === 0 && (
                <p className="mt-2.5 px-3 py-2 rounded-[10px] bg-state-warn-soft border border-state-warn/30 text-[12px] leading-[1.5] text-state-warn text-pretty">
                    <strong>Aucun plat n'est encore autorisé.</strong> Vos convives ouvriraient un lien sans rien
                    à choisir. Ouvrez la configuration du menu et cochez « Illimité », ou indiquez un nombre,
                    pour chaque plat que vous proposez.
                </p>
            )}
            <Link to={`/menus-convives/${eventId}/menu`} className={`${pillSecondary(32)} mt-2.5`}>
                {menus.length === 0 ? 'Configurer le menu' : 'Changer de formule'}
            </Link>
        </section>
    );
}

export interface CollecteStats { importes: number; invites: number; reponses: number; silencieux: number; nonInvites: number; envoyeLe?: string | null }

/** « La collecte des réponses » (maquette 18b) : quatre lignes chiffrées, chacune avec sa barre de ton. */
export function CollecteReponsesCard({ s }: { s: CollecteStats }) {
    const lignes: { label: string; detail: string; n: number; barre: string; fg: string }[] = [
        { label: 'Convives importés', detail: 'Nom, prénom et adresse — rien de plus.', n: s.importes, barre: 'bg-state-info', fg: 'text-state-info' },
        { label: 'Invitations envoyées', detail: s.envoyeLe ? `Dernier envoi le ${fmtDateHeure(s.envoyeLe)}, par lots de 25.` : s.nonInvites > 0 ? `${s.nonInvites} convive${s.nonInvites > 1 ? 's' : ''} pas encore invité${s.nonInvites > 1 ? 's' : ''}.` : 'Par lots de 25.', n: s.invites, barre: 'bg-state-ok', fg: 'text-content' },
        { label: 'Réponses reçues', detail: 'Chaque convive choisit son plat lui-même.', n: s.reponses, barre: 'bg-state-ok', fg: 'text-content' },
        { label: 'Sans réponse', detail: 'Relançables depuis le bouton « Relancer ».', n: s.silencieux, barre: 'bg-state-warn', fg: 'text-state-warn' },
    ];
    return (
        <section className="p-4 rounded-xl bg-surface-soft border border-border-soft">
            <div className={`${SURTITRE} mb-3`}>La collecte des réponses</div>
            <div className="flex flex-col">
                {lignes.map((l, i) => (
                    <div key={l.label} className={`flex items-center gap-3 py-2.5 ${i > 0 ? 'border-t border-border-soft' : ''}`}>
                        <span className={`w-1 h-[26px] shrink-0 rounded-full ${l.barre}`} />
                        <div className="flex-1 min-w-0">
                            <div className="text-[12.5px] font-semibold text-content">{l.label}</div>
                            <div className="mt-px text-[11px] text-content-muted text-pretty">{l.detail}</div>
                        </div>
                        <span className={`text-[17px] font-bold tabular-nums shrink-0 ${l.fg}`}>{l.n}</span>
                    </div>
                ))}
            </div>
            <p className="mt-3 pt-[11px] border-t border-border-soft text-[11.5px] leading-[1.5] text-content-muted text-pretty">
                Le fichier du client ne contient que <span className="font-mono">Nom</span>, <span className="font-mono">Prénom</span> et <span className="font-mono">Email</span>.
                Le plat, la contrainte alimentaire et le refus viennent ensuite du convive lui-même.
            </p>
        </section>
    );
}

/**
 * « Comptage cuisine » (maquette 18b) : ce chiffre part en cuisine à la validation de la liste. Les deux
 * documents (fiche technique cuisine, récap convives) et la case « Allergies » de l'ancien en-tête vivent
 * ici : c'est de ce comptage qu'ils sont tirés.
 */
/** « 1 couverts » se lisait sous les trois tuiles : un couvert reste au singulier. */
const uniteCouverts = (n: number) => (Math.abs(n) >= 2 ? 'couverts' : 'couvert');

export function ComptageCuisineCard({ reponses, couvertsDevis, contraintes, occupe, includeAllergies, onIncludeAllergies, onFicheTechnique, onRecap }: {
    reponses: number; couvertsDevis: number; contraintes: number;
    occupe: '' | 'fiche' | 'recap';
    includeAllergies: boolean; onIncludeAllergies: (v: boolean) => void;
    onFicheTechnique: () => void; onRecap: () => void;
}) {
    return (
        <section className="p-4 rounded-xl bg-surface border border-border">
            <div className="flex items-baseline justify-between gap-2.5 flex-wrap mb-[13px]">
                <span className={SURTITRE}>Comptage cuisine</span>
                <span className="text-[11.5px] text-content-muted">Ce chiffre part en cuisine à la validation de la liste.</span>
            </div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-2.5">
                <TuileChiffre grande t={{ label: 'Réponses fermes', valeur: reponses, unite: uniteCouverts(reponses), ton: 'succes', note: 'Plats choisis par les convives eux-mêmes.' }} />
                <TuileChiffre grande t={{ label: 'Couverts au devis', valeur: couvertsDevis || '—', unite: couvertsDevis ? uniteCouverts(couvertsDevis) : undefined, ton: 'neutre', note: "L'écart sera facturé selon le réel." }} />
                <TuileChiffre grande t={{ label: 'Assiettes adaptées', valeur: contraintes, unite: uniteCouverts(contraintes), ton: 'danger', note: 'Contraintes déclarées par le convive, jamais par le client.' }} />
            </div>
            <div className="flex items-center gap-2.5 flex-wrap mt-3.5 pt-3 border-t border-border-soft">
                <button type="button" onClick={onFicheTechnique} disabled={!!occupe} className={pillSecondary(32)}>
                    {occupe === 'fiche' ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Fiche technique cuisine
                </button>
                <Checkbox checked={includeAllergies} onChange={(e) => onIncludeAllergies(e.target.checked)} label="Avec les allergies" className="!text-[12px]" />
                <button type="button" onClick={onRecap} disabled={!!occupe} className={pillSecondary(32)}>
                    {occupe === 'recap' ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Récap convives (client)
                </button>
            </div>
        </section>
    );
}
